import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Check,
  ChevronLeft,
  Heart,
  RefreshCw,
  Share2,
  Sliders,
  Sparkles,
  X,
} from "lucide-react";
import { Eyebrow, Wordmark } from "@/components/editorial";
import { ApiError, api } from "@/lib/api";
import { useFavorites } from "@/features/favorites/useFavorites";
import { useEntitlements, usePaywall } from "@/features/paywall";
import { BrigadeProgress } from "@/features/menu/BrigadeProgress";
import { PremiumFiltersSheet } from "@/features/menu/PremiumFiltersSheet";
import {
  DraftPendingBanner,
  SwapPendingBanner,
} from "@/features/menu/SwapPendingBanner";
import { useMealSwap } from "@/features/menu/useMealSwap";
import { ThemeSelectionSheet } from "@/features/menu/ThemeSelectionSheet";
import {
  getThemePreference,
  setThemePreference,
} from "@/features/menu/themePreference";
import { shareMenu } from "@/lib/share";
import { getFoodImageUrl } from "@/lib/stock-food-images";
import {
  DAY_LABELS_SHORT,
  formatPrice,
  formatWeekRange,
  nutriColor,
  parseISODate,
} from "@/features/menu/menuLabels";
import { useMenu } from "@/features/menu/store";
import { useBrigadeStream } from "@/hooks/useBrigadeStream";
import { MENU_LATEST_KEY, useMenuPlan } from "@/hooks/useMenuPlan";
import { cn } from "@/lib/utils";
import type {
  MenuPlan,
  NutriScore,
  PremiumFilters,
  Recipe,
  ValidatedMenu,
} from "@/types/menu";

const DAY_LETTERS = ["D", "L", "M", "M", "J", "V", "S"] as const;

/**
 * Weekly menu — consumes a real MenuPlan from the store / preview endpoint.
 * Premium-gated controls (swap a meal) open the paywall for free users.
 */
export function MenuPage() {
  const {
    plan,
    isPreview,
    isCurrentWeek,
    serverResolved,
    hasCurrentWeekServerMenu,
    isLoading,
    isError,
    error,
  } = useMenuPlan();
  const hydrated = useMenu((s) => s._hydrated);
  const setPlan = useMenu((s) => s.setPlan);
  const paywall = usePaywall();
  const { isPremium } = useEntitlements();
  const setMenuId = useMenu((s) => s.setMenuId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Premium filter state — applied on the next regenerate.
  const [filters, setFilters] = useState<PremiumFilters | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Theme selection — persisted preference for weekly batch theme
  const [theme, setTheme] = useState<string | null>(() => getThemePreference());
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);

  function handleThemeSelect(themeId: string | null) {
    setTheme(themeId);
    setThemePreference(themeId);
  }

  // Phase 13a — generation mode (personalised vs surprise).
  // The theme selection sheet replaces the old inline toggle; genMode
  // still flows to the brigade as the mode_perso flag.
  const genMode: "personalised" | "surprise" = (() => {
    const stored = localStorage.getItem("bc.generation_mode");
    return stored === "personalised" || stored === "surprise"
      ? stored
      : "personalised";
  })();

  // Live streaming brigade: SSE replaces the old fire-and-await POST so
  // the user sees agent-level progress during the 30-60 s generation.
  const brigade = useBrigadeStream();

  // Phase 12 — favourites.
  const { isFavorite, toggle: toggleFav } = useFavorites();

  useEffect(() => {
    if (brigade.plan) {
      // Phase 12b — reveal-and-curate: instead of auto-validating,
      // set the plan in the reveal store and navigate to the reveal page.
      setPlan(brigade.plan);
      navigate("/menu/reveal");
    }
  }, [brigade.plan, setPlan, navigate]);

  // P23 — auto-trigger brigade generation after onboarding (?generate=true).
  // Waits for the persisted store and the server menu to resolve, then
  // generates unless this week's menu is already there (audit M16: keyed on
  // isCurrentWeek, not `!plan` — last week's persisted plan or the demo
  // preview must not suppress this week's generation).
  const generateRequested = params.get("generate") === "true";
  const generateHandled = useRef(false);
  const startBrigade = brigade.start;
  const brigadeRunning = brigade.running;
  useEffect(() => {
    if (!generateRequested || generateHandled.current) return;
    // A local plan makes isLoading false before /latest answered: wait for
    // the server too, or a menu validated on another device this week would
    // be regenerated (and its id dropped) before it is adopted.
    if (!hydrated || isLoading || !serverResolved || brigadeRunning) return;
    generateHandled.current = true;
    // Remove the param so it doesn't re-trigger
    setParams({}, { replace: true });
    if (isCurrentWeek || hasCurrentWeekServerMenu) return;
    setMenuId(null);
    void startBrigade({ generation_mode: genMode, theme });
  }, [
    generateRequested,
    hydrated,
    isLoading,
    serverResolved,
    isCurrentWeek,
    hasCurrentWeekServerMenu,
    brigadeRunning,
    startBrigade,
    setParams,
    setMenuId,
    genMode,
    theme,
  ]);

  // Premium single-dinner swap (audit C1): keeps menuId and never persists
  // on its own — the user confirms via the SwapPendingBanner.
  const mealSwap = useMealSwap("current");

  const validate = useMutation({
    mutationFn: (args: { draftSessionId?: string | null }) => {
      const qs = args.draftSessionId
        ? `?draft_session_id=${encodeURIComponent(args.draftSessionId)}`
        : "";
      return api<ValidatedMenu>(`/api/v1/menu/validate${qs}`, {
        method: "POST",
        body: plan,
      });
    },
    onSuccess: (res) => {
      setPlan(res.plan);
      setMenuId(res.menu_id ?? null);
      // Prime the server-menu cache so it agrees with the store we just wrote
      // (single-writer invariant, see useMenuPlan). Without this the adoption
      // effect would later see a stale serverMenuId and overwrite this plan
      // (audit H-7/H-8).
      queryClient.setQueryData(MENU_LATEST_KEY, res);
      toast.success(
        res.persisted
          ? "Menu validé et enregistré"
          : "Menu validé (enregistrement local uniquement)",
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        // The api() helper already signs the user out on a dead session.
        return;
      } else if (err instanceof ApiError && err.status === 409) {
        toast.error("Veuillez d'abord compléter votre profil");
        navigate("/onboarding");
      } else {
        toast.error((err as Error).message);
      }
    },
  });

  // A draft is persisted ONLY on an explicit user action (BRD §5, audit
  // M14): the reveal's "Valider" (which lands here with ?validate=true and
  // the draft_session_id, so the backend can mark which slug replaced each
  // discard) or the "Valider mon menu" banner below. A draft that merely
  // lands on /menu is never saved behind the user's back — for a free user
  // that silently burned the weekly quota.
  // Never for a swapped menu (`hasPendingSwap`, persisted in the store):
  // that draft is saved only through the swap banner (audit C1). Preview
  // plans (demo data) must never be persisted as the user's real menu.
  useEffect(() => {
    if (params.get("validate") !== "true") return;
    if (!plan || isPreview) return;
    if (validate.isPending) return;
    const draftSessionId = params.get("draft_session_id");
    setParams({}, { replace: true });
    if (plan.status === "validated" || mealSwap.hasPendingSwap) return;
    validate.mutate({ draftSessionId });
  }, [
    params,
    plan,
    isPreview,
    validate.isPending,
    validate.mutate,
    setParams,
    mealSwap.hasPendingSwap,
  ]);

  const isValidated = plan?.status === "validated";
  const hasUnsavedDraft =
    !!plan && !isPreview && !isValidated && !mealSwap.hasPendingSwap;

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="flex items-center justify-between border-b border-hair px-5 py-4">
        <Link
          to="/dashboard"
          aria-label="Accueil"
          className="text-foreground hover:text-accent"
        >
          <ChevronLeft className="size-5" strokeWidth={1.25} />
        </Link>
        <Wordmark size="sm" />
        <div className="flex items-center gap-3">
          <FiltersButton
            isPremium={isPremium}
            activeCount={countActive(filters)}
            onOpen={() => {
              if (isPremium) setFiltersOpen(true);
              else
                paywall.open({
                  feature:
                    "Filtres Nutri / Éco / saisonnier et plafond calorique font partie de l'offre Cahier.",
                });
            }}
          />
          {/* Theme selection pill — opens the ThemeSelectionSheet */}
          <button
            type="button"
            onClick={() => setThemeSheetOpen(true)}
            aria-label="Choisir le thème de la semaine"
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors",
              theme && theme !== "surprise"
                ? "border-accent bg-accent/10 text-accent"
                : "border-hair text-ink-50 hover:border-ink-30",
            )}
          >
            {theme && theme !== "surprise" ? (
              <>
                <Sparkles className="size-3" strokeWidth={2} />
                Thème
              </>
            ) : (
              <>
                <Sparkles className="size-3" strokeWidth={2} />
                Surprise
              </>
            )}
          </button>
          {brigade.running ? (
            <button
              aria-label="Annuler la régénération"
              onClick={brigade.abort}
              className="text-foreground hover:text-destructive"
            >
              <X className="size-[18px]" strokeWidth={1.25} />
            </button>
          ) : (
            <button
              aria-label="Régénérer la semaine"
              onClick={() => {
                setMenuId(null);
                void brigade.start({
                  premium: filters,
                  generation_mode: genMode,
                  theme,
                });
              }}
              className="text-foreground hover:text-accent"
            >
              <RefreshCw className="size-[18px]" strokeWidth={1.25} />
            </button>
          )}
          {/* Phase 14 — share button */}
          {plan && (
            <button
              aria-label="Partager le menu"
              onClick={() => {
                const mid = useMenu.getState().menuId;
                if (!mid) {
                  toast.error(
                    "Validez d'abord votre menu avant de le partager.",
                  );
                  return;
                }
                void shareMenu(mid, "menu");
              }}
              className="text-foreground hover:text-accent"
            >
              <Share2 className="size-[18px]" strokeWidth={1.25} />
            </button>
          )}
        </div>
      </header>

      <PremiumFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        initial={filters}
        onApply={(next) => {
          setFilters(next);
          toast.success("Filtres appliqués — appuyez sur ↻ pour régénérer.");
        }}
      />

      <ThemeSelectionSheet
        open={themeSheetOpen}
        onOpenChange={setThemeSheetOpen}
        onSelect={handleThemeSelect}
        currentTheme={theme}
      />

      {(brigade.running || brigade.error || brigade.usageRollup.calls > 0) && (
        <div className="border-b border-hair px-5 py-4">
          <BrigadeProgress
            currentRole={brigade.currentRole}
            completedRoles={brigade.completedRoles}
            error={brigade.error}
            usageByRole={brigade.usageByRole}
            usageRollup={brigade.usageRollup}
            usageSummary={brigade.usageSummary}
          />
        </div>
      )}

      {isLoading && !plan && (
        <p className="px-6 py-12 text-center text-[13px] text-ink-50">
          Chargement…
        </p>
      )}
      {isError && !plan && (
        <p className="px-6 py-12 text-center text-[13px] text-destructive">
          {(error as Error).message}
        </p>
      )}

      {plan && (
        <>
          {hasUnsavedDraft && (
            <DraftPendingBanner
              onValidate={() => validate.mutate({ draftSessionId: null })}
              isValidating={validate.isPending}
            />
          )}
          {mealSwap.hasPendingSwap && (
            <SwapPendingBanner
              onSave={mealSwap.save}
              isSaving={mealSwap.isSaving}
            />
          )}
          <MenuHero plan={plan} validated={isValidated} />
          {/* DayStrip is commented out — the per-card date label already
              communicates "which day" so the picker is redundant. The
              component is kept defined below for future reuse elsewhere
              in the app (e.g. shopping or weekly overview screens). */}
          {/* <DayStrip plan={plan} /> */}
          <MealList
            plan={plan}
            onSwap={mealSwap.swap}
            isSwapping={mealSwap.isSwapping}
            isFavorite={isFavorite}
            onToggleFav={toggleFav}
            onPaywall={() =>
              paywall.open({
                feature:
                  "Remplacer un repas avec l'IA fait partie de l'offre Cahier.",
              })
            }
          />
        </>
      )}
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────
function MenuHero({ plan, validated }: { plan: MenuPlan; validated: boolean }) {
  // `calories` is already a per-serving figure (OFF enrichment divides by
  // servings), so sum it as-is — dividing again under-counted the weekly total.
  // Matches RecipeCard / RecipeDetailPage / SharedMenuPage.
  const totalKcal = plan.days.reduce((sum, d) => {
    const meals = Object.values(d.meals).filter(Boolean) as Recipe[];
    return sum + meals.reduce((mealSum, r) => mealSum + (r.calories ?? 0), 0);
  }, 0);
  const NUTRI_ORDER: Record<NutriScore, number> = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
    E: 4,
  };
  const worstScore = plan.days
    .flatMap((d) => Object.values(d.meals).filter(Boolean) as Recipe[])
    .reduce<NutriScore | null>((worst, r) => {
      if (!r.nutri_score) return worst;
      if (!worst) return r.nutri_score;
      return NUTRI_ORDER[r.nutri_score] > NUTRI_ORDER[worst]
        ? r.nutri_score
        : worst;
    }, null);
  const nutri: NutriScore | null = worstScore;
  return (
    <section className="border-b border-hair px-6 py-6">
      <div className="flex items-start justify-between gap-3">
        {plan.week_start && (
          <Eyebrow className="mb-2">
            {`Semaine ${weekNumber(plan.week_start)} · ${formatWeekRange(
              plan.week_start,
              plan.days.length,
            )}`}
          </Eyebrow>
        )}
        {validated && (
          <span className="inline-flex items-center gap-1 rounded-pill bg-accent/15 px-2.5 py-1 text-[10.5px] uppercase tracking-[0.18em] text-accent">
            <Check className="size-3" strokeWidth={2.5} /> Validé
          </span>
        )}
      </div>
      <h1 className="h-display text-[40px] leading-[1] sm:text-[44px]">
        {plan.theme ? (
          <span className="italic">{plan.theme}</span>
        ) : (
          "Cuisine du moment"
        )}
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[12px] text-ink-50">
        <span>{totalKcal.toLocaleString("fr-FR")} kcal</span>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", nutriColor(nutri))} />
          Nutri {nutri}
        </span>
        {plan.estimated_cost_eur != null && (
          <span>{formatPrice(plan.estimated_cost_eur)} estimés</span>
        )}
      </div>
    </section>
  );
}

// ─── Day strip ────────────────────────────────────────────────────────
// Currently unrendered (see the comment in the page body). Exported so
// the build doesn't strip it — the design is reusable: a horizontal
// day picker that could live on the shopping or weekly-overview screen.
export function DayStrip({ plan }: { plan: MenuPlan }) {
  return (
    <div className="flex gap-2 overflow-x-auto border-b border-hair px-6 py-4">
      {plan.days.map((d, i) => {
        const date = parseISODate(d.day);
        const letter = DAY_LETTERS[date.getDay()];
        const num = date.getDate();
        return (
          <button
            key={d.day}
            className={cn(
              "flex w-12 shrink-0 flex-col items-center rounded-pill border px-2 py-2 transition-colors",
              i === 0
                ? "border-foreground bg-foreground text-background"
                : "border-hair bg-background text-foreground",
            )}
          >
            <span className="text-[9px] uppercase tracking-[0.2em] opacity-70">
              {letter}
            </span>
            <span className="font-serif text-[16px]">{num}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Meal list ────────────────────────────────────────────────────────
function MealList({
  plan,
  onSwap,
  isSwapping,
  isFavorite,
  onToggleFav,
  onPaywall,
}: {
  plan: MenuPlan;
  onSwap: (day: string, slug: string) => void;
  isSwapping: boolean;
  isFavorite: (slug: string) => boolean;
  onToggleFav: (
    slug: string,
    kind?: "meal" | "batch",
    recipe?: Recipe,
  ) => Promise<void>;
  onPaywall: () => void;
}) {
  return (
    <div className="flex-1 overflow-auto px-5 py-4">
      <ul className="grid gap-3">
        {plan.days.map((d) => {
          const recipe = d.meals.dinner;
          if (!recipe) return null;
          return (
            <li key={d.day}>
              <MealCard
                date={d.day}
                recipe={recipe}
                onSwap={() => onSwap(d.day, recipe.slug)}
                disabled={isSwapping}
                isFav={isFavorite(recipe.slug)}
                onToggleFav={() => onToggleFav(recipe.slug, "meal", recipe)}
              />
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={onPaywall}
        className="mt-4 flex w-full items-center gap-3 border border-dashed border-accent bg-accent/5 px-4 py-3.5 text-left transition-colors hover:bg-accent/10"
      >
        <Sparkles className="size-4 text-accent" strokeWidth={1.5} />
        <div className="flex-1 text-[12.5px] text-ink-70">
          <span className="font-serif italic text-accent">Premium · </span>
          Remplacer un repas avec l'IA, filtres Nutri / Éco, partage foyer
        </div>
        <span className="text-[12px] font-semibold text-accent">→</span>
      </button>
    </div>
  );
}

// ─── Meal card ────────────────────────────────────────────────────────
// Plain non-swipeable card. The swipe-to-discard gesture lives in the
// reveal-and-curate flow (Phase 12b); duplicating it here on the
// validated menu was confusing — both directions used to mean "discard"
// while the reveal screen uses left/right asymmetrically — and made
// accidental gestures during scroll trigger costly LLM regenerations.
// The deliberate post-validation actions are favourite (heart) and
// "Remplacer ce repas" (refresh — premium-gated).
function MealCard({
  date,
  recipe,
  onSwap,
  disabled,
  isFav,
  onToggleFav,
}: {
  date: string;
  recipe: Recipe;
  onSwap: () => void;
  disabled?: boolean;
  isFav: boolean;
  onToggleFav: () => void;
}) {
  const { t } = useTranslation();
  const d = parseISODate(date);
  const totalMin = recipe.prep_minutes + recipe.cook_minutes;
  return (
    <article className="group relative flex items-stretch gap-3.5 border border-hair bg-background p-3 transition-colors hover:border-foreground">
      <Link
        to={`/recipe/${recipe.slug}`}
        className="flex flex-1 items-stretch gap-3.5"
      >
        <div className="flex w-14 flex-col items-center justify-center border-r border-hair pr-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-50">
            {DAY_LABELS_SHORT[d.getDay()]}
          </div>
          <div className="font-serif text-2xl font-normal tracking-[-0.01em]">
            {d.getDate()}
          </div>
        </div>
        <div className="h-[70px] w-[70px] shrink-0 overflow-hidden rounded-sm bg-hair">
          <img
            src={getFoodImageUrl(recipe.slug)}
            alt={recipe.title}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[9.5px] uppercase tracking-[0.18em] text-ink-50">
            {recipe.nutri_score ? `Nutri ${recipe.nutri_score}` : "—"}
          </div>
          <div className="font-serif text-[16px] leading-[1.15]">
            {recipe.title}
          </div>
          <div className="mt-1.5 flex gap-2 font-mono text-[11px] text-ink-50">
            <span>{totalMin}'</span>
            <span>·</span>
            <span>
              {recipe.servings}
              {t("reveal.servings_abbr")}
            </span>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onToggleFav();
        }}
        aria-label={isFav ? t("reveal.remove_fav") : t("reveal.add_fav")}
        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-ink-50 opacity-0 transition-all hover:bg-accent/10 hover:text-accent group-hover:opacity-100"
      >
        <Heart
          className="size-3.5"
          strokeWidth={1.5}
          fill={isFav ? "currentColor" : "none"}
        />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onSwap();
        }}
        disabled={disabled}
        aria-label={t("reveal.replace_meal")}
        className="absolute right-10 top-2 flex h-8 w-8 items-center justify-center rounded-full text-ink-50 opacity-0 transition-opacity hover:bg-accent/10 hover:text-accent group-hover:opacity-100 disabled:opacity-50"
      >
        <RefreshCw
          className={cn("size-3.5", disabled && "animate-spin")}
          strokeWidth={1.5}
        />
      </button>
    </article>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function weekNumber(iso: string): number {
  const d = parseISODate(iso);
  const onejan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(
    ((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
  );
}

function FiltersButton({
  isPremium,
  activeCount,
  onOpen,
}: {
  isPremium: boolean;
  activeCount: number;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={
        isPremium ? "Filtres premium" : "Filtres premium — abonnement requis"
      }
      className={cn(
        "relative flex items-center gap-1.5 text-foreground hover:text-accent",
        !isPremium && "text-ink-50",
      )}
    >
      <Sliders className="size-[18px]" strokeWidth={1.25} />
      {!isPremium && (
        <Sparkles className="size-3 text-accent" strokeWidth={2} />
      )}
      {activeCount > 0 && (
        <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-semibold text-accent-foreground">
          {activeCount}
        </span>
      )}
    </button>
  );
}

function countActive(p: PremiumFilters | null): number {
  if (!p) return 0;
  return (
    Number(Boolean(p.nutri_min)) +
    Number(Boolean(p.eco_min)) +
    Number(Boolean(p.seasonal_only)) +
    Number(typeof p.max_kcal_per_meal === "number")
  );
}
